<template >

  <div class="container-general card">
    <form class="general">
      <load-content :loading="isBusy && isReady == false">
        <h4 class="pl5">{{ $t("project.form.configuration.general_settings") }}</h4>
        <hr class="lines" />
        <div class="section">
          <label>{{ $t("project.form.configuration.article_summary") }}</label>
          <input-toggle
            :checked="model.article_summary"
            :onChange="(value) => updateForm(value, 'article_summary')"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <label>{{ $t("project.form.configuration.auto_publish") }}</label>
          <input-toggle
            :checked="model.auto_publish"
            :onChange="(value) => updateForm(value, 'auto_publish')"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <label>{{ $t("project.form.configuration.auto_approve_tags") }}</label>
          <input-toggle
            :checked="model.auto_approve_tags"
            :onChange="(value) => updateForm(value, 'auto_approve_tags')"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <label>{{ $t("project.form.configuration.auto_approve_authors") }}</label>
          <input-toggle
            :checked="model.auto_approve_authors"
            :onChange="(value) => updateForm(value, 'auto_approve_authors')"
          />
        </div>
        <hr class="lines" />
        <div class="section" :class="{loading: scoringTogglerBusy}">
          <label>{{ $t("project.form.configuration.scoring") }}</label>
          <InputSelect
            :value="model.scoring"
            :allow-empty="false"
            :show-labels="false"
            :options="scoringOptions"
            :loading="scoringTogglerBusy"
            option-value="id"
            option-label="method"
            class="input-select"
            :onChange="(value) => updateForm(value, 'scoring')"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <label>{{ $t("project.form.configuration.use_outlet_circulation") }}</label>
          <input-toggle
            :checked="model.use_outlet_circulation"
            :onChange="(value) => updateForm(value, 'use_outlet_circulation')"
          />
        </div>
        <hr class="lines" />
        <div class="section d-flex justify-content-between align-items-center">
          <div class="d-inline-flex align-items-center">
            <label style="white-space: nowrap;">{{ $t("project.form.configuration.organizations_with_category") }}</label>
            <InputSelect
              :value="model.organizations_category_id"
              :allow-empty="false"
              :show-labels="false"
              :options="organizationsCategories"
              :disabled="!model.organizations_with_category || isOrganizationsCategoryBusy || projectCategorySetupIsLoading"
              option-value="id"
              option-label="name"
              class="input-select mx-2 mt-1"
              :onChange="(value) => updateOrgCate(value)"
            />
            <button type="submit" class="btn btn-carma btn-sm mb-0" :disabled="!model.organizations_with_category || isOrganizationsCategoryBusy || hasCategory || projectCategorySetupIsLoading" style="width: 25%" @click.prevent="() => { showOrganizationCategoryConfirmation = true; }">
              <span v-if="isOrganizationsCategoryBusy">
                <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                <span class="visually-hidden" role="status">Loading...</span>
              </span>
              {{ $t('form.button.save') }}
            </button>
          </div>
          <input-toggle
            :checked="model.organizations_with_category"
            :onChange="(value) => updateForm(value, 'organizations_with_category')"
            :disabled="isOrganizationsCategoryBusy"
          />
        </div>
        <hr class="lines"/>
        <div class="section">
          <label>
            {{ $t("project.form.configuration.ave_for_social") }}
            <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.ave_for_social_info')}`"></i>
          </label>
          <input-toggle
            :checked="model.ave_for_social"
            :onChange="(value) => updateForm(value, 'ave_for_social')"
            :disabled="!model.enable_social_ave_toggle"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <div class="d-flex align-items-baseline">
            <label>
              {{ $t("project.form.configuration.import_tags_from_insight") }}
              <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.tooltip_import_tags_from_insight')}`"></i>
            </label>
            <div class="mx-3">
              <button type="button" class="btn btn-carma btn-sm m-0" :class="{ 'pe-none': !model.import_tags_from_insight }" :disabled="!model.import_tags_from_insight" @click="onClickImport">{{ $t('tag.form.import.button') }}</button>
            </div>
          </div>
          <input-toggle
            :checked="model.import_tags_from_insight"
            :onChange="(value) => updateForm(value, 'import_tags_from_insight')"
          />
        </div>
        <hr class="lines" />

        <h4 class="pl5 pt-20">{{ $t("project.form.configuration.automated_tags") }}</h4>
        <hr class="lines" />
        <div class="section">
          <label>{{ $t("project.form.configuration.display_automated_tags") }}</label>
          <input-toggle
            :checked="model.display_automated_tags"
            :onChange="(value) => updateForm(value, 'display_automated_tags')"
          />
        </div>
        <hr class="lines" />

        <h4 class="pl5 pt-20">{{ $t("project.form.configuration.ai_gpt") }}</h4>
        <hr class="lines" />
        <div class="section">
          <label>
            {{ $t("project.form.configuration.auto_coder") }}
            <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.tooltip_auto_coder')}`"></i>
          </label>
          <input-toggle
            :checked="model.auto_coder"
            :onChange="(value) => updateForm(value, 'auto_coder')"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <label>
            {{ $t("project.form.configuration.auto_en_translation") }} 
            <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.tooltip_auto_en_translation')}`"></i>
          </label>
          <input-toggle
            :checked="model.auto_translation"
            :onChange="(value) => updateForm(value, 'auto_translation')"
          />
        </div>
        <hr class="lines" />
        <div class="section">
          <div class="d-inline-flex align-items-center">
            <label style="white-space: nowrap;">{{ $t("project.form.configuration.auto_ai_summary") }}
              <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.tooltip_auto_ai_summary')}`"></i>
            </label>
          </div>
          <input-toggle
            :checked="model.auto_ai_summary && carmaAiSummaries.length > 0"
            :onChange="(value) => updateForm(value, 'auto_ai_summary')"
            :disabled="projectCategorySetupIsLoading || carmaAiSummariesLoading || carmaAiSummaries.length === 0"
          />
        </div>
        <hr class="lines" />
        
        <h4 class="pl5 pt-20">{{ $t("project.form.configuration.social_data") }}</h4>
        <hr class="lines" />
        <div class="section">
          <label>
            {{ $t("project.form.configuration.enable_socials_data") }}
            <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.tooltip_enable_socials_data')}`"></i>
          </label>
          <input-toggle
            :checked="model.enable_socials_data"
            :onChange="(value) => updateForm(value, 'enable_socials_data')"
          />
        </div>

        <template v-if="socialsEnabled">
          <hr class="lines" />
          <div class="section">
            <label>
              {{ $t("project.form.configuration.display_social_metrics") }}
              <i class="fas fa-info-circle icon-grey pr5" v-tooltip="`${$t('project.form.configuration.tooltip_display_social_metrics')}`"></i>
            </label>
            <input-toggle
              :checked="model.display_social_metrics"
              :onChange="(value) => updateForm(value, 'display_social_metrics')"
            />
          </div>
        </template>
        <hr class="lines" />

        <confirmation-modal 
          v-if="showOrganizationCategoryConfirmation"
          :headerText="$t('messages.coding.title')"
          :message="$t('messages.coding.message')"
          messageClasses="text-danger"
          :showDeleteButton="false"
          :size="''"
          :isLoading="isOrganizationsCategoryBusy"
          @confirmAction="updateForm(model.organizations_category_id, 'organizations_category_id', 'isOrganizationsCategoryBusy')"
          @cancelAction="() => { showOrganizationCategoryConfirmation = false; }" 
        />

      </load-content>
    </form>

    <side-panel v-if="showImportConfirm" :onClose="onClosePanel" :title="$t('tag.form.import.title')">
      <load-content :loading="importing">
        <p>{{ $t('tag.form.import.text') }}</p>
        <div class="d-flex justify-content-end mt-5">
          <button class="btn btn-carma m-0" :class="{ 'pe-none': importing }" :disabled="importing" @click="onConfirmImport">{{ $t('form.button.continue') }}</button>
        </div>
      </load-content>
    </side-panel>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Axios from 'axios';
import _ from 'lodash';

import { SCORING_METHODS } from '@/config/const';

import LoadContent from '@/components/load-content';
import InputSelect from '@/components/form-components/input-select';
import InputToggle from '@/components/form-components/input-toggle';
import confirmationModal from '@/components/confirmation-modal';
import SidePanel from '@/components/side-panel';

export default {
  name: 'General',
  components: {
    InputSelect,
    LoadContent,
    InputToggle,
    confirmationModal,
    SidePanel
  },
  mounted() {
    this.getCarmaAiSummaries({projectId: this.projectId, pagination: {scope: 'coding'}});
    this.isReady = false;
    this.model = {
      article_summary: this.projectSetup?.general.enable_socials_data || this.projectSetup?.general.article_summary,
      auto_publish: this.projectSetup?.general?.auto_publish,
      use_outlet_circulation: this.projectSetup?.general?.use_outlet_circulation,
      auto_approve_tags: this.projectSetup?.general?.auto_approve_tags,
      auto_approve_authors: this.projectSetup?.general?.auto_approve_authors,
      display_automated_tags: this.projectSetup?.general?.display_automated_tags,
      auto_coder: this.projectSetup?.general?.auto_coder,
      auto_relevancy: this.projectSetup?.general?.auto_relevancy,
      auto_translation: this.projectSetup?.general?.auto_translation,
      scoring: this.projectSetup?.general?.scoring,
      display_social_metrics: this.projectSetup?.general?.display_social_metrics,
      enable_socials_data: this.projectSetup?.general?.enable_socials_data,
      organizations_with_category: this.projectSetup?.general?.organizations_with_category,
      organizations_category_id: this.projectSetup?.general?.organizations_category_id,
      import_tags_from_insight: this.projectSetup?.general?.import_tags_from_insight,
      enable_social_ave_toggle: this.projectSetup?.general?.enable_social_ave_toggle,
      ave_for_social: this.projectSetup?.general?.ave_for_social,
      auto_ai_summary: this.projectSetup?.general?.auto_ai_summary
    }
    setTimeout(() => {
      this.isReady = true;
    }, 10);
  },
  computed: {
    ...mapState({
      projectSetup: state => state.projectSetup.projectSetup.data || {},
      projectCategorySetup: state => state.projectSetup.projectCategorySetup?.data || [],
      projectCategorySetupIsLoading: state => state.projectSetup?.projectCategorySetup?.isBusy,
      isBusy: state => state.projectSetup.projectSetup.isBusy,
      all: state =>
        state.projectSetup.projectSetup.data
          ? state.projectSetup.projectSetup.data.general
          : {},
      carmaAiSummaries: state => state.carmaAiSummaries?.carmaAiSummaries?.data || [],
      carmaAiSummariesLoading: state => state.carmaAiSummaries?.carmaAiSummaries.isBusy,
    }),
    scoringOptions() {
      const methods = this.all.scoring_methods;

      if (!methods || !Array.isArray(methods)) return [];

      return _.orderBy(this.all.scoring_methods, ['method'], ['asc']);
    },
    socialsEnabled () {
      return this.projectSetup.general.enable_socials_dashboard === true;
    },
    organizationsCategories () {
      return this.projectCategorySetup?.filter(c => c?.tags?.length < 10); // omit the category which it's tags more than 10
    },
    hasCategory(){
      return this.model.organizations_category_id && !this.model.organizations_with_category;
    },
    projectId () {
      return this.$route.params.project_uid;
    }
  },
  methods: {
    ...mapActions({
      getprojectSetup: "projectSetup/getprojectSetup",
      updateGeneral: "projectSetup/updateGeneral",
      updateProjectSetup: "projectSetup/updateProjectSetup",
      getCarmaAiSummaries: "carmaAiSummaries/getCarmaAiSummaries",
    }),
    updateForm (value, property, isLoadingProp) {
      if (this.isReady) {
        if (property == 'scoring') {
          this.scoringTogglerBusy = true;
        }

        let isOrgCate = 'organizations_with_category' === property;

        if(isLoadingProp) this[isLoadingProp] = true;

        this.updateValue(value, property);
        const payload = this.updateData();

        if(!isOrgCate || (isOrgCate && value === false && this.all.organizations_with_category)) {
          this.apiRequest({ payload }).finally(() => {
            if(this[isLoadingProp]) this[isLoadingProp] = false;
            this.showOrganizationCategoryConfirmation = false;
          });
        }
      }
    },
    updateValue (value, key){
      this.model[key] = value;
    },
    updateData (model = this.model) {
      return Object.assign({}, _.cloneDeep(this.all), model);
    },
    updateOrgCate(value = ''){
      if(value === this.model.organizations_category_id) return;
      this.updateValue(value, 'organizations_category_id');
    },
    async apiRequest ({ payload, cb }) {
      return await this.updateProjectSetup({ projectUid: this.projectUid, payload }).then(r => {
        this.scoringTogglerBusy = false;
        if(typeof cb === 'function') cb(r);
      });
    },

    onClosePanel () {
      this.showImportConfirm = false;
    },

    onClickImport () {
      this.showImportConfirm = true;
    },

    onConfirmImport () {
      this.importing = true;
      Axios.post('analysis/projects/' + this.projectId + '/upsert_insight_tags').then(r => {
        const list = r.data.data;
        this.onClosePanel();
        this.$notify({ group: 'store_notifications', type: 'success', text: this.$tc('tag.form.import.success', list.length, { count: list.length }) });
      }).catch(error => {
        this.$notify({ group: 'store_notifications', type: 'error', text: this.$t('messages.request.error') });
      }).finally(() => {
        this.importing = false;
      });
    },
  },
  data: function () {
    const projectUid = this.$route.params.project_uid;
    return {
      projectUid,
      isReady: false,
      scoringTogglerBusy: false,
      isOrganizationsCategoryBusy: false,
      showOrganizationCategoryConfirmation: false,
      model: {
        article_summary: false,
        auto_publish: false,
        use_outlet_circulation: false,
        auto_approve_tags: false,
        auto_approve_authors: false,
        display_automated_tags: false,
        auto_coder: false,
        auto_relevancy: false,
        auto_translation: false,
        scoring: SCORING_METHODS.NONE,
        display_social_metrics: false,
        import_tags_from_insight: false
      },

      showImportConfirm: false,
      importing: false
    };
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
@import "../../styles/patterns/_multiselect.scss";
</style>
<style lang="scss" scoped>

  :deep(.multiselect__spinner) {
    margin-top: -7px;
  }

  .container-general {
    display: flex;
    flex: 1;
    flex-direction: column;
    position: relative;
    min-height: 60px;
  }

  .general {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  .pt-20 {
    padding-top: 20px;
  }

  .section {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-left: 18px;
    margin-right: 30px;
  }

  .lines {
    margin-top: 8px;
    margin-bottom: 8px;
    margin-left: 18px;
    margin-right: 25px;
  }

  label {
    font-weight: bold;
    margin-left: 10px;
  }

  .disclaimer {
    margin-top: 20px;
    margin-bottom: 4px;
    small {
      font-size: 0.8em;
      color: #888;
    }
  }
</style>
